import { queryOptions, useQuery } from '@tanstack/react-query';

import { getAllProducts, getFeaturedProducts, getProductById } from '@/lib/api/product';
import type { ProductWithCategory, ProductsResponse } from '@/types/product.types';

import { queryKeys } from './query-keys-factory';

/**
 * Denormalize products response: join categories back onto products
 */
function denormalize(response: ProductsResponse): Array<ProductWithCategory> {
  const categoriesById = new Map(response.categories.map((c) => [c.id, c]));
  return response.products.map((product) => ({
    ...product,
    category: categoriesById.get(product.category_id) ?? null,
  }));
}

/**
 * Query Options Factories
 */
export const productQueries = {
  /**
   * Get all products with categories
   */
  all: () =>
    queryOptions({
      queryKey: queryKeys.products.list(),
      queryFn: getAllProducts,
      select: denormalize,
      staleTime: 1000 * 60 * 10, // 10 minutes
      retry: false,
    }),

  /**
   * Get featured products (for homepage)
   */
  featured: () =>
    queryOptions({
      queryKey: queryKeys.products.list({ featured: true }),
      queryFn: getFeaturedProducts,
      select: denormalize,
      staleTime: 1000 * 60 * 10,
      retry: false,
    }),

  /**
   * Get product by ID
   */
  byId: (id: string) =>
    queryOptions({
      queryKey: queryKeys.products.detail(id),
      queryFn: () => getProductById(id),
      staleTime: 1000 * 60 * 10,
      enabled: !!id,
      retry: false,
    }),
};

/**
 * Query Hooks
 */

/**
 * Hook to fetch all products
 */
export const useGetAllProducts = () => useQuery(productQueries.all());

/**
 * Hook to fetch featured products for homepage
 */
export const useGetFeaturedProducts = () => useQuery(productQueries.featured());

/**
 * Hook to fetch product by ID
 */
export const useGetProductById = (id: string) => useQuery(productQueries.byId(id));
